import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderNothing, withProps, withHandlers } from 'recompose';
import { Button, ButtonToolbar, Clearfix, Glyphicon, Nav, NavItem } from 'react-bootstrap';
import _ from 'lodash';


import TicketReview from './TicketReview';


import linkWithCredentials from '../../../utils/linkWithCredentials';
import {
  selectTicketId, deferJobId, unreviewJobId, sendJobToSage, setErrorMessage, clearErrorMessage, bypassSage,
} from '../actions';
import { openInNewTab, querySelector, sleep } from '../../../utils';
import queryMetaHandler from '../../../utils/queryMetaHandler';
import omitProps from '../../../utils/omitProps';
import { getApiServerUrl } from '../../configuration/selectors';
import { isApolloGraphQLError } from '../../../utils/errorTypeCheck';


import UnreviewReasonModal, {
  unreviewReasonModalPropTypes,
  withUnreviewReasonModalState,
} from './UnreviewReasonModal';

import SageInfoModal, {
  sageInfoModalPropTypes,
  withSageInfoModalState,
} from './SageInfoModal';

import BypassModal, {
  withBypassModalState,
  bypassModalPropTypes,
} from './BypassModal';

import ConfirmModal, {
  withConfirmModalState,
  confirmModalPropTypes,
} from '../../confirmModal/confirmModal';

import { dispatchUpdateLease } from '../../admin/leaseList/actions';
import { getTicketLink } from '../../ticketViewer/links';
import Overlay from '../../../components/Overlay';
import { tryHandleGraphQLError } from '../../../utils/graphQLErrorHandlers';
import { getErrorMessage } from '../selectors';
import { downloadingFileAndUpdatingCredentials } from '../../../utils/downloadUpdatingCredentials';

const nodeType = PropTypes.shape({
  id: PropTypes.number,
});


const propTypes = exact({
  ...unreviewReasonModalPropTypes,
  ...sageInfoModalPropTypes,
  ...confirmModalPropTypes,
  ...bypassModalPropTypes,

  jobId: PropTypes.number.isRequired,
  tickets: PropTypes.arrayOf(nodeType).isRequired,
  job: nodeType.isRequired,
  selectedTicketId: PropTypes.number.isRequired,
  selectTicketIdAction: PropTypes.func.isRequired,

  deferJobIdAction: PropTypes.func.isRequired,
  unreviewJobIdAction: PropTypes.func.isRequired,
  apiServerUrl: PropTypes.string.isRequired,
  deferJob: PropTypes.func.isRequired,
  unreviewJob: PropTypes.func.isRequired,
  updateLeaseAction: PropTypes.func.isRequired,

  createJobInvoiceAction: PropTypes.func.isRequired,

  errorMessage: PropTypes.string,
  setErrorMessageAction: PropTypes.func.isRequired,
  clearErrorMessageAction: PropTypes.func.isRequired,
});


const JobInvoiceReview = (props) => {
  const { selectTicketIdAction } = props;

  const {
    showUnreviewReasonModal, closeUnreviewReasonModal, openUnreviewReasonModal,
  } = props;

  const {
    showSageInfoModal,
    openSageInfoModal,
    closeSageInfoModal,
    clickedDeferJobInfoModal,
  } = props;

  const renderTab = (ticket) => {
    const { id, ticket_number: ticketNumber } = ticket;
    const handleClick = () => { selectTicketIdAction(id); };
    return (
      <NavItem key={id} eventKey={id} onClick={handleClick}>{ticketNumber.number}</NavItem>
    );
  };

  const {
    apiServerUrl,
    job,
    tickets,
    selectedTicketId,
    unreviewReasonDisabled,
    unreviewReasonError,
    deferJob,
    createJobInvoiceAction,
    unreviewJob,
    setUnreviewReasonError,
    updateLeaseAction,
    bypassSageAction,
    setErrorMessageAction,
    clearErrorMessageAction,
    errorMessage,
  } = props;

  const { closeModal, showModal, openModal } = props;
  const { closeBypassModal, showBypassModal, openBypassModal } = props;

  const handlePdfDownload = () => {
    const pdfUrl = linkWithCredentials(`${apiServerUrl}${job.pdf_path}`);
    downloadingFileAndUpdatingCredentials(pdfUrl);
  };

  const handlePromptSageInfo = async (currentJob, clickedDefer = false) => {
    const { sage_info: sageInfo } = currentJob;

    let jobId = currentJob.id;

    if (sageInfo) {
      try {
        if (clickedDefer) {
          jobId = await deferJob(currentJob.id);
        } else {
          await createJobInvoiceAction(currentJob.id);
        }
        openInNewTab(getTicketLink(selectedTicketId, jobId, false));
      } catch (e) {
        const [message] = tryHandleGraphQLError(e);
        const eMessage = `${message}  -  Contact support and try again later.`;
        setErrorMessageAction(eMessage);
        openModal();
      }
    } else {
      openSageInfoModal(clickedDefer);
    }
  };


  const disableDefer = !job.can_defer;
  const {
    lease,
    sageSendable,
    promptForReturnToManager,
  } = job;

  const disableDeferReason = (tickets.length > 1) ? "Can't defer multi ticket job" : 'Remove from Sage First';

  return (
    <div>
      <Nav bsStyle="tabs" activeKey={selectedTicketId}>
        {tickets.map(renderTab, this)}
      </Nav>
      <div className="panel panel-default panel-tabbed">
        <TicketReview ticketId={selectedTicketId} />
        <div className="panel-footer">
          <Clearfix>
            <ButtonToolbar className="pull-left">
              <Button bsStyle="info" onClick={handlePdfDownload}>
                <Glyphicon glyph="export" style={{ marginRight: '0.5em' }} />
                Export Tickets as PDF
              </Button>
            </ButtonToolbar>
            <ButtonToolbar className="pull-right">
              <div className="inline-btn-styling">
                <Overlay
                  enabled={promptForReturnToManager}
                  message="Remove from Sage First"
                >
                  <div className="tooltip-area">
                    <Button
                      bsStyle="danger"
                      disabled={promptForReturnToManager}
                      onClick={openUnreviewReasonModal}
                    >
                      <Glyphicon glyph="send" style={{ marginRight: '0.5em' }} />
                      Return to Field Admin
                    </Button>
                  </div>
                </Overlay>
              </div>
              <div className="inline-btn-styling">
                <Button
                  bsStyle="warning"
                  disabled={!sageSendable}
                  onClick={openBypassModal}
                >
                  Bypass Sage
                </Button>
              </div>
              <div className="inline-btn-styling">
                <Overlay
                  enabled={disableDefer}
                  message={disableDeferReason}
                >
                  <div className="pull-left">
                    <Button
                      bsStyle="warning"
                      disabled={disableDefer}
                      onClick={async () => {
                        await sleep(500);
                        return handlePromptSageInfo(job, true);
                      }}
                    >
                      <Glyphicon glyph="time" style={{ marginRight: '0.5em' }} />
                      { /* NOTE: Eslint is not styling the text inside the html
                          eslint rules need to be updated to fix this issue */ }
                      Defer to Month-Lease
                    </Button>
                  </div>
                </Overlay>
              </div>
              <div className="inline-btn-styling">
                <Overlay
                  enabled={!sageSendable}
                  message="Already in Sage"
                >
                  <div className="tooltip-area">
                    <Button
                      disabled={!sageSendable}
                      bsStyle="primary"
                      onClick={async () => {
                        await sleep(500);
                        return handlePromptSageInfo(job);
                      }}
                    >
                      <Glyphicon glyph="envelope" style={{ marginRight: '0.5em' }} />
                      Send to Sage
                    </Button>
                  </div>
                </Overlay>
              </div>
            </ButtonToolbar>
          </Clearfix>
          <UnreviewReasonModal
            show={showUnreviewReasonModal}
            onHide={closeUnreviewReasonModal}
            onSubmit={unreviewJob}
            disabled={unreviewReasonDisabled}
            error={unreviewReasonError}
            setError={setUnreviewReasonError}
          />
          <SageInfoModal
            ticketId={selectedTicketId}
            job={job}
            lease={lease}
            show={showSageInfoModal}
            onHide={closeSageInfoModal}
            onSubmit={updateLeaseAction}
            createJobInvoiceAction={createJobInvoiceAction}
            deferJob={deferJob}
            clickedDeferJob={clickedDeferJobInfoModal}
          />
          <ConfirmModal
            title="Error Sending To Sage"
            message={errorMessage}
            confirmButtonText="OK"
            confirmAction={() => {
              clearErrorMessageAction();
              closeModal();
            }}
            showDismissButton={false}
            onHide={closeModal}
            show={showModal}
          />
          <BypassModal
            title="Confirm bypassing of Sage"
            message="Bypassing of Sage requires manual creating of SO Invoice inside of Sage."
            confirmButtonText="OK"
            confirmAction={async () => {
              try {
                await bypassSageAction(job.id);
                closeBypassModal();
              } catch (e) {
                closeBypassModal();
                const [message] = tryHandleGraphQLError(e);
                setErrorMessageAction(message);
                openModal();
              }
            }}
            showDismissButton={false}
            onHide={closeBypassModal}
            show={showBypassModal}
          />
        </div>
      </div>
    </div>
  );
};


JobInvoiceReview.propTypes = propTypes;


const TicketsOnJobInvoiceReviewGQL = gql`
  query TicketsOnJobInvoiceReviewQuery($jobId: Int!) {
    all_tickets(job_id: $jobId, date_order: asc) {
      edges {
        node {
          id
          ticket_number {
            id
            number
          }
          date {
            display
          }
        }
      }
    }
    job(job_id: $jobId) {
      id
      invoice_state
      sageSendable: sage_sendable
      sage_info
      promptForReturnToManager: prompt_for_return_to_manager
      pdf_path
      can_defer
      lease {
        id
        name
        tax_schedule {
          id
          description
        }
        customer {
          id
          customer_name
        }
      }
    }
  }
`;


const mapDataToProps = withProps((props) => {
  const { data } = props;
  const tickets = querySelector(data, '$.all_tickets.edges[*].node', []);
  const { job } = data;
  return {
    tickets,
    job,
    data,
  };
});


const mapStateToProps = (state) => {
  return {
    jobId: state.ticketInvoicing.jobId,
    selectedTicketId: state.ticketInvoicing.ticketId,
    apiServerUrl: getApiServerUrl(state),
    errorMessage: getErrorMessage(state),
  };
};


const mapDispatchToProps = {
  selectTicketIdAction: selectTicketId,
  deferJobIdAction: deferJobId,
  unreviewJobIdAction: unreviewJobId,
  createJobInvoiceAction: sendJobToSage,
  updateLeaseAction: dispatchUpdateLease,
  setErrorMessageAction: setErrorMessage,
  clearErrorMessageAction: clearErrorMessage,
  bypassSageAction: bypassSage,
};


const withData = graphql(TicketsOnJobInvoiceReviewGQL);


const withRedux = connect(mapStateToProps, mapDispatchToProps);


const withHideNoJobId = branch(
  (props) => { return props.jobId === null; },
  renderNothing,
);

/*
* Make sure not to render until a ticketId is available. This allows all children components to
* mark ticketId as a required propType and greatly simplifies their rendering logic.
 */
const withHideNoTicketId = branch(
  (props) => { return props.selectedTicketId === null; },
  renderNothing,
);


const withDeferJob = withHandlers({
  deferJob: (props) => {
    return async () => {
      const { jobId, deferJobIdAction } = props;
      const res = await deferJobIdAction(jobId);
      return res;
    };
  },
});


const withUnreviewJob = withHandlers({
  unreviewJob: (props) => {
    return async (reason) => {
      const {
        jobId,
        unreviewJobIdAction,
        setUnreviewReasonDisabled,
        setUnreviewReasonError,
        clearUnreviewReasonError,
        closeUnreviewReasonModal,
      } = props;

      clearUnreviewReasonError();
      setUnreviewReasonDisabled(true);
      try {
        await unreviewJobIdAction(jobId, reason);
      } catch (e) {
        if (isApolloGraphQLError(e)) {
          setUnreviewReasonDisabled(false);
          setUnreviewReasonError(e.message);
          return;
        }
        throw e;
      }
      setUnreviewReasonDisabled(false);
      closeUnreviewReasonModal();
    };
  },
});


const withSelectFirstTicket = lifecycle({
  componentWillMount() {
    const { tickets, selectTicketIdAction } = this.props;
    if (tickets.length > 0) {
      selectTicketIdAction(tickets[0].id);
    }
  },
  componentWillReceiveProps(nextProps) {
    const { tickets, selectTicketIdAction } = this.props;
    const { tickets: nextTickets } = nextProps;
    if (_.isEqual(tickets, nextTickets)) {
      return;
    }
    if (nextTickets.length > 0) {
      selectTicketIdAction(nextTickets[0].id);
    }
  },
});


const enhance = compose(
  withRedux,
  withHideNoJobId,
  withUnreviewReasonModalState,
  withSageInfoModalState,
  withConfirmModalState,
  withBypassModalState,
  withDeferJob,
  withUnreviewJob,
  withData,
  queryMetaHandler,
  mapDataToProps,
  omitProps(['data']),
  withSelectFirstTicket,
  withHideNoTicketId,
);


export default enhance(JobInvoiceReview);
